import React, { useMemo, useRef, useState } from "react";
import { Modal } from "antd";
import { Ico } from "components/Uikit";
import { AudioTrack } from "providers/AudioProvider/types";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { useUiLang } from "utils/uiLanguage";
import { addCueWindow, formatWindowTime } from "../../cueActions";

interface CreateSubtitleModalProps {
    track: AudioTrack;
}

/**
 * «+» — распознать речь в промежутке после этой реплики и добавить её новой репликой.
 *
 * Промежуток — от конца этой реплики (end_rus_milli) до начала следующей, и диалог называет его
 * до миллисекунды: раньше он говорил «в этом интервале», а сам интервал строился от
 * «начало + длина» и на испорченной реплике уезжал на минуты. Пока запрос идёт, повторное
 * нажатие ничего не делает — переводчики жали «+» снова и снова и получали дубли. Итог (новая
 * реплика в фокусе или причина отказа) показывает провайдер.
 */
export const CreateIntervalSubtitle = ({ track }: CreateSubtitleModalProps) => {
    const state = useAudioStateContext();
    const handles = useAudioHandlesContext();
    const en = useUiLang() === "en";
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const busyRef = useRef(false);

    const gap = useMemo(
        () =>
            addCueWindow({
                track,
                tracks: state.translationTrackAll[state.originalLang] || [],
                durationFull: state.durationFull,
            }),
        [track, state.translationTrackAll, state.originalLang, state.durationFull],
    );
    const refusal = "error" in gap ? gap.error : null;

    const onClose = () => {
        if (busyRef.current) return;
        setOpen(false);
    };

    const handleSubmit = async () => {
        if (busyRef.current || "error" in gap) return;
        busyRef.current = true;
        setBusy(true);
        try {
            await handles.createRusSubInterval({ start: gap.start, end: gap.end });
        } finally {
            busyRef.current = false;
            setBusy(false);
            setOpen(false);
        }
    };

    const text =
        "error" in gap
            ? gap.error
            : en
              ? `Recognise the speech between ${formatWindowTime(gap.start)} and ${formatWindowTime(gap.end)} ` +
                `(${((gap.end - gap.start) / 1000).toFixed(1)} s) and add it as a new cue?`
              : `Распознать речь в промежутке ${formatWindowTime(gap.start)} – ${formatWindowTime(gap.end)} ` +
                `(${((gap.end - gap.start) / 1000).toFixed(1).replace(".", ",")} с) и добавить её новой репликой?`;

    return (
        <div
            className="x_save"
            title={en ? "Add a cue in the gap after this one" : "Добавить реплику в промежуток после этой"}
            style={busy ? { opacity: 0.5, cursor: "wait" } : undefined}
        >
            <Ico onClick={() => !busyRef.current && setOpen(true)}>add</Ico>
            <Modal
                open={open}
                title={en ? "Add a cue" : "Добавить реплику"}
                onCancel={onClose}
                onOk={handleSubmit}
                okText={en ? "Add" : "Добавить"}
                cancelText={en ? "Cancel" : "Отмена"}
                confirmLoading={busy}
                okButtonProps={{ disabled: Boolean(refusal) }}
                cancelButtonProps={{ disabled: busy }}
                maskClosable={!busy}
            >
                <div style={refusal ? { color: "#cf1322" } : undefined}>{text}</div>
            </Modal>
        </div>
    );
};
